import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageSeo } from '@/hooks/usePageSeo';
import { SITE_URL } from '@/config/site';
import { topics } from '@/mocks/topics';
import { formatHeat } from '@/utils/format';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicTimeline from '@/pages/topics/detail/components/TopicTimeline';
import ViewpointGrid from '@/pages/topics/detail/components/ViewpointGrid';
import CommentSection from '@/pages/topics/detail/components/CommentSection';
import RelatedTopics from '@/pages/topics/detail/components/RelatedTopics';

const legend = [
  { key: 'support', label: '支持', color: 'bg-primary-500' },
  { key: 'neutral', label: '中立', color: 'bg-secondary-400' },
  { key: 'oppose', label: '反对', color: 'bg-accent-500' },
];

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((t) => t.id === id);

  const jsonLd = useMemo(() => {
    if (!topic) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: topic.title,
        description: topic.summary,
        inLanguage: 'zh-CN',
        datePublished: topic.publishedAt,
        dateModified: topic.publishedAt,
        articleSection: topic.category,
        keywords: topic.tags.join(','),
        image: topic.cover,
        author: { '@type': 'Organization', name: '吃瓜观察局' },
        publisher: { '@type': 'Organization', name: '吃瓜观察局', url: SITE_URL },
        mainEntityOfPage: `${SITE_URL}/topics/${topic.id}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: '话题广场', item: `${SITE_URL}/topics` },
          { '@type': 'ListItem', position: 3, name: topic.title, item: `${SITE_URL}/topics/${topic.id}` },
        ],
      },
    ];
  }, [topic]);

  usePageSeo({
    title: topic ? `${topic.title}｜吃瓜观察局` : '话题不存在｜吃瓜观察局',
    description: topic
      ? `${topic.summary.slice(0, 96)}`
      : '该话题可能已经下线或链接有误，返回话题广场查看当前正在发酵的热门讨论。',
    keywords: topic ? `${topic.category},${topic.tags.join(',')},网友讨论,舆论风向` : '热搜话题,话题广场',
    path: topic ? `/topics/${topic.id}` : '/topics',
    ogType: 'article',
    ogImage: topic?.cover,
    jsonLd,
  });

  if (!topic) {
    return (
      <section className="mx-auto flex w-full max-w-3xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-ghost-line" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">这个瓜已经不在了</h1>
        <p className="mt-3 text-sm text-foreground-600">你访问的话题可能已经下线，或者链接不太对。去看看现在大家都在聊什么吧。</p>
        <Link
          to="/topics"
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回话题广场
        </Link>
      </section>
    );
  }

  return (
    <>
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb
            items={[{ label: '首页', to: '/' }, { label: '话题广场', to: '/topics' }, { label: topic.category, to: `/topics?category=${encodeURIComponent(topic.category)}` }, { label: topic.title }]}
          />
          <div className="mt-5 flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-primary-100 px-3 py-1 font-medium text-primary-700">{topic.category}</span>
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-fire-line text-primary-500" />
              热度 {formatHeat(topic.heat)}
            </span>
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-time-line" />
              发酵 {topic.evolveHours} 小时
            </span>
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-calendar-line" />
              {topic.publishedAt}
            </span>
          </div>
          <h1 className="mt-4 max-w-4xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {topic.title}
          </h1>
        </div>
      </div>

      <section className="w-full bg-background-50 py-10 md:py-14">
        <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="w-full overflow-hidden rounded-lg">
              <img
                src={topic.cover}
                alt={`${topic.title} - 话题配图`}
                className="h-56 w-full object-cover md:h-80"
              />
            </div>

            <p className="mt-6 rounded-lg border-l-4 border-primary-400 bg-background-100 p-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              {topic.summary}
            </p>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <i className="ri-git-commit-line text-primary-500" />
                事件脉络
              </h2>
              <p className="mt-2 text-sm text-foreground-500">按公开信息出现的先后顺序整理，便于快速了解发酵过程。</p>
              <div className="mt-6">
                <TopicTimeline items={topic.timeline} />
              </div>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <i className="ri-scales-3-line text-primary-500" />
                各方观点
              </h2>
              <p className="mt-2 text-sm text-foreground-500">把讨论中反复出现的立场归类，帮助你快速理解分歧在哪里。</p>
              <div className="mt-6">
                <ViewpointGrid items={topic.viewpoints} />
              </div>
            </div>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <i className="ri-chat-3-line text-primary-500" />
                评论区
              </h2>
              <p className="mt-2 text-sm text-foreground-500">
                共 {topic.comments.length} 条精选留言，挑了几条有代表性的观点，你也可以加入讨论。
              </p>
              <div className="mt-6">
                <CommentSection comments={topic.comments} />
              </div>
            </div>

            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/topics"
                className="flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回话题广场
              </Link>
              <Link
                to="/"
                className="flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                看看热榜
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </article>

          <aside className="flex flex-col gap-6 lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-semibold text-foreground-950">话题数据</h2>
              <dl className="mt-4 grid grid-cols-2 gap-4">
                <div className="rounded-md bg-background-100 p-3">
                  <dt className="text-xs text-foreground-500">当前热度</dt>
                  <dd className="mt-1 font-heading text-lg font-bold text-primary-600">{formatHeat(topic.heat)}</dd>
                </div>
                <div className="rounded-md bg-background-100 p-3">
                  <dt className="text-xs text-foreground-500">发酵时长</dt>
                  <dd className="mt-1 font-heading text-lg font-bold text-foreground-900">{topic.evolveHours}h</dd>
                </div>
              </dl>

              <h3 className="mt-5 text-sm font-semibold text-foreground-900">观点分布</h3>
              <div className="mt-3 flex h-3 w-full overflow-hidden rounded-full bg-background-200">
                <div className="bg-primary-500" style={{ width: `${topic.stats.support}%` }} />
                <div className="bg-secondary-400" style={{ width: `${topic.stats.neutral}%` }} />
                <div className="bg-accent-500" style={{ width: `${topic.stats.oppose}%` }} />
              </div>
              <ul className="mt-3 space-y-1.5 text-xs text-foreground-600">
                {legend.map((l) => (
                  <li key={l.key} className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-sm ${l.color}`} />
                      {l.label}
                    </span>
                    <span>
                      {l.key === 'support'
                        ? topic.stats.support
                        : l.key === 'neutral'
                          ? topic.stats.neutral
                          : topic.stats.oppose}
                      %
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-semibold text-foreground-950">话题标签</h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {topic.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/topics?keyword=${encodeURIComponent(tag)}`}
                    className="cursor-pointer rounded-md bg-background-100 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="text-base font-semibold text-foreground-950">相关话题</h2>
              <div className="mt-4">
                <RelatedTopics currentId={topic.id} currentCategory={topic.category} />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}